import type { DashboardCardType, DashboardLayoutItem, DashboardSplitSlot } from "../../../../types/dashboardLayout"

/** Always full-width dashboard sections (not splittable into half rows). */
export const DASHBOARD_PINNED_FULL_WIDTH_CARD_TYPES: DashboardCardType[] = [
  "tradeActivityCalendar",
  "tradeDistribution",
]

/**
 * Dashboard chart rows: always added full width, then optionally paired side-by-side via drag.
 * Trade Outcomes = `performanceBreakdown` in the registry.
 */
export const DASHBOARD_SPLITTABLE_CHART_ROW_CARD_TYPES: DashboardCardType[] = [
  "drawdownOverTime",
  "equityPeakPath",
  "streakAnalysis",
  "longVsShortPerformance",
  "performanceBreakdown",
  "keyRiskMetrics",
  "pnlOverTime",
]

export type DashboardChartRow =
  | { kind: "full"; order: number; cardType: DashboardCardType }
  | { kind: "split"; order: number; left: DashboardCardType; right: DashboardCardType }

export function isSplittableChartRowCard(cardType: DashboardCardType): boolean {
  return DASHBOARD_SPLITTABLE_CHART_ROW_CARD_TYPES.includes(cardType)
}

export function isPinnedFullWidthCard(cardType: DashboardCardType): boolean {
  return DASHBOARD_PINNED_FULL_WIDTH_CARD_TYPES.includes(cardType)
}

function sortChartRowCards(a: DashboardLayoutItem, b: DashboardLayoutItem): number {
  if (a.slot === "left" && b.slot !== "left") return -1
  if (b.slot === "left" && a.slot !== "left") return 1
  return a.cardType.localeCompare(b.cardType)
}

function getSplittablePeersAtOrder(items: DashboardLayoutItem[], order: number): DashboardLayoutItem[] {
  return items.filter(
    (item) =>
      item.zone === "fullWidth" &&
      item.visible &&
      isSplittableChartRowCard(item.cardType) &&
      (item.order ?? 0) === order
  )
}

export function buildDashboardChartRows(items: DashboardLayoutItem[]): DashboardChartRow[] {
  const byOrder = new Map<number, DashboardLayoutItem[]>()

  for (const item of items) {
    if (item.zone !== "fullWidth" || !item.visible || !isSplittableChartRowCard(item.cardType)) {
      continue
    }
    const order = item.order ?? 0
    const group = byOrder.get(order) ?? []
    group.push(item)
    byOrder.set(order, group)
  }

  const rows: DashboardChartRow[] = []
  const sortedOrders = Array.from(byOrder.keys()).sort((a, b) => a - b)
  for (const order of sortedOrders) {
    const group = [...(byOrder.get(order) ?? [])].sort(sortChartRowCards)
    if (group.length >= 2) {
      rows.push({
        kind: "split",
        order,
        left: group[0].cardType,
        right: group[1].cardType,
      })
      continue
    }
    if (group.length === 1) {
      rows.push({ kind: "full", order, cardType: group[0].cardType })
    }
  }

  return rows
}

export function getNextDashboardChartRowOrder(items: DashboardLayoutItem[]): number {
  const orders = items
    .filter((item) => item.zone === "fullWidth" && item.visible && isSplittableChartRowCard(item.cardType))
    .map((item) => item.order ?? 0)
  return orders.length ? Math.max(...orders) + 1 : 0
}

export function coalesceSplitChartRows(items: DashboardLayoutItem[]): DashboardLayoutItem[] {
  const byOrder = new Map<number, DashboardLayoutItem[]>()

  for (const item of items) {
    if (item.zone !== "fullWidth" || !item.visible || !isSplittableChartRowCard(item.cardType)) {
      continue
    }
    const order = item.order ?? 0
    const group = byOrder.get(order) ?? []
    group.push(item)
    byOrder.set(order, group)
  }

  return items.map((item) => {
    if (item.zone !== "fullWidth" || !item.visible || !isSplittableChartRowCard(item.cardType)) {
      return item
    }
    const order = item.order ?? 0
    const peers = byOrder.get(order) ?? []
    if (peers.length === 1) {
      const { slot: _slot, ratio: _ratio, ...rest } = item
      return rest
    }
    return item
  })
}

function mergeChartRowDrag(
  items: DashboardLayoutItem[],
  activeId: DashboardCardType,
  overId: DashboardCardType,
  targetOrder: number
): DashboardLayoutItem[] {
  const overItem = items.find((item) => item.cardType === overId)
  if (!overItem) return items

  const anchorSlot: DashboardSplitSlot = overItem.slot ?? "left"
  const incomingSlot: DashboardSplitSlot = anchorSlot === "left" ? "right" : "left"

  const nextItems = items.map((item) => {
    if (item.cardType === overId) {
      return {
        ...item,
        order: targetOrder,
        slot: anchorSlot,
        ratio: 0.5,
      }
    }
    if (item.cardType === activeId) {
      return {
        ...item,
        order: targetOrder,
        slot: incomingSlot,
        ratio: 0.5,
      }
    }
    return item
  })

  return coalesceSplitChartRows(nextItems)
}

function swapChartRowCards(
  items: DashboardLayoutItem[],
  activeId: DashboardCardType,
  overId: DashboardCardType
): DashboardLayoutItem[] {
  const activeItem = items.find((item) => item.cardType === activeId)
  const overItem = items.find((item) => item.cardType === overId)
  if (!activeItem || !overItem) return items

  return coalesceSplitChartRows(
    items.map((item) => {
      if (item.cardType === activeId) {
        return {
          ...item,
          order: overItem.order,
          slot: overItem.slot,
          ratio: overItem.slot ? 0.5 : item.ratio,
        }
      }
      if (item.cardType === overId) {
        return {
          ...item,
          order: activeItem.order,
          slot: activeItem.slot,
          ratio: activeItem.slot ? 0.5 : item.ratio,
        }
      }
      return item
    })
  )
}

export interface ChartRowMergePreview {
  anchorCardType: DashboardCardType
  incomingCardType: DashboardCardType
}

/** Live drag preview: dragging one solo chart row onto another will pair them side-by-side. */
export function getChartRowMergePreview(
  items: DashboardLayoutItem[],
  activeId: string | number | null | undefined,
  overId: string | number | null | undefined
): ChartRowMergePreview | null {
  if (activeId == null || overId == null || activeId === overId) return null

  const incomingCardType = activeId as DashboardCardType
  const anchorCardType = overId as DashboardCardType
  if (!isSplittableChartRowCard(incomingCardType) || !isSplittableChartRowCard(anchorCardType)) {
    return null
  }

  const incomingItem = items.find((item) => item.cardType === incomingCardType)
  const anchorItem = items.find((item) => item.cardType === anchorCardType)
  if (!incomingItem?.visible || !anchorItem?.visible) return null

  const incomingOrder = incomingItem.order ?? 0
  const anchorOrder = anchorItem.order ?? 0
  if (incomingOrder === anchorOrder) return null

  const anchorPeers = getSplittablePeersAtOrder(items, anchorOrder)
  if (anchorPeers.length >= 2) return null

  return { anchorCardType, incomingCardType }
}

/** Drag a splittable chart onto another to pair rows, swap within a row, or swap positions. */
export function applyChartRowDragEnd(
  items: DashboardLayoutItem[],
  activeId: DashboardCardType,
  overId: DashboardCardType
): DashboardLayoutItem[] {
  const activeItem = items.find((item) => item.cardType === activeId)
  const overItem = items.find((item) => item.cardType === overId)
  if (!activeItem || !overItem) return items
  if (!isSplittableChartRowCard(activeId) || !isSplittableChartRowCard(overId)) return items

  const activeOrder = activeItem.order ?? 0
  const overOrder = overItem.order ?? 0

  if (activeOrder === overOrder) {
    const peers = getSplittablePeersAtOrder(items, activeOrder)
    if (peers.length < 2) return items

    return coalesceSplitChartRows(
      items.map((item) => {
        if (item.cardType === activeId) {
          return { ...item, slot: overItem.slot, ratio: 0.5 }
        }
        if (item.cardType === overId) {
          return { ...item, slot: activeItem.slot, ratio: 0.5 }
        }
        return item
      })
    )
  }

  const overPeers = getSplittablePeersAtOrder(items, overOrder)

  if (overPeers.length >= 2) {
    return swapChartRowCards(items, activeId, overId)
  }

  return mergeChartRowDrag(items, activeId, overId, overOrder)
}

export const CHART_ROW_INSERT_DROP_PREFIX = "chart-row-insert:"

export function chartRowInsertDropId(insertIndex: number): string {
  return `${CHART_ROW_INSERT_DROP_PREFIX}${insertIndex}`
}

export function parseChartRowInsertDropId(id: string | number): number | null {
  const value = String(id)
  if (!value.startsWith(CHART_ROW_INSERT_DROP_PREFIX)) return null
  const index = Number(value.slice(CHART_ROW_INSERT_DROP_PREFIX.length))
  return Number.isFinite(index) && index >= 0 ? index : null
}

/** Insert a splittable chart as its own row at `insertIndex` (0 = before the first row). */
export function applyChartRowInsertDrag(
  items: DashboardLayoutItem[],
  activeId: DashboardCardType,
  insertIndex: number
): DashboardLayoutItem[] {
  if (!isSplittableChartRowCard(activeId)) return items

  const rows = buildDashboardChartRows(items)
  const groups: DashboardCardType[][] = rows.map((row) =>
    row.kind === "full" ? [row.cardType] : [row.left, row.right]
  )

  let sourceGroupIndex = -1
  let sourceCardIndex = -1
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
    const cardIndex = groups[groupIndex].indexOf(activeId)
    if (cardIndex !== -1) {
      sourceGroupIndex = groupIndex
      sourceCardIndex = cardIndex
      break
    }
  }
  if (sourceGroupIndex === -1) return items

  const sourceGroup = groups[sourceGroupIndex]
  sourceGroup.splice(sourceCardIndex, 1)
  if (sourceGroup.length === 0) {
    groups.splice(sourceGroupIndex, 1)
    if (sourceGroupIndex < insertIndex) {
      insertIndex -= 1
    }
  } else if (sourceGroupIndex < insertIndex) {
    insertIndex -= 1
  }

  const boundedIndex = Math.max(0, Math.min(insertIndex, groups.length))
  groups.splice(boundedIndex, 0, [activeId])

  const placement = new Map<DashboardCardType, { order: number; slot?: DashboardSplitSlot }>()
  groups.forEach((group, order) => {
    if (group.length >= 2) {
      placement.set(group[0], { order, slot: "left" })
      placement.set(group[1], { order, slot: "right" })
      return
    }
    if (group.length === 1) {
      placement.set(group[0], { order })
    }
  })

  return coalesceSplitChartRows(
    items.map((item) => {
      const nextPlacement = placement.get(item.cardType)
      if (!nextPlacement) return item
      return {
        ...item,
        visible: item.cardType === activeId ? true : item.visible,
        zone: "fullWidth" as const,
        order: nextPlacement.order,
        slot: nextPlacement.slot,
        ratio: nextPlacement.slot ? 0.5 : undefined,
      }
    })
  )
}

/** Add a splittable chart on its own full-width row (default for the add-widget flow). */
export function addSplittableChartRowCard(
  items: DashboardLayoutItem[],
  cardType: DashboardCardType,
  order?: number
): DashboardLayoutItem[] {
  if (!isSplittableChartRowCard(cardType)) return items

  const nextOrder = order ?? getNextDashboardChartRowOrder(items)
  let nextItems = items.map((item) =>
    item.cardType === cardType
      ? {
          ...item,
          visible: true,
          zone: "fullWidth" as const,
          order: nextOrder,
          slot: undefined,
          ratio: undefined,
        }
      : item
  )

  if (!nextItems.some((item) => item.cardType === cardType)) {
    nextItems.push({
      cardType,
      visible: true,
      zone: "fullWidth",
      order: nextOrder,
    })
  }

  return coalesceSplitChartRows(nextItems)
}
